import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { resolveThumbnails, GraphConsentError } from './graphUpload';

/**
 * SharePoint (Graph) hooks shared by the photo features: the site photo
 * thread (#560) and the punch list (#518). Moved here unchanged from
 * SitePhotoThread.tsx so both use the same token and preview rules.
 */

// Delegated scope, acquired ON DEMAND (NOT part of loginRequest).
const GRAPH_SCOPES = ['Files.ReadWrite.All'];

/**
 * Two ways to get a SharePoint token:
 *  - `interactive` may open the sign-in popup, so only call it from a tap
 *    (posting, downloading, "Show previews") — phones block popups that
 *    aren't, and an unasked-for popup on page load is confusing anyway;
 *  - `silent` never does, and is what previews use. When it can't get a
 *    token, `needsSignIn` flips so the thread can offer the tap.
 */
/**
 * Medium previews, cached PER LINK and fetched only for links not yet known.
 *
 * ⚠️ Not keyed by page (review of #560). A page's list of links shifts by one
 * whenever a post is added or removed, and TanStack refetches on window focus
 * — which on a phone is every return from the camera — so a per-page key
 * changed on almost every refresh and every tile flashed back to its file
 * name while up to hundreds of lookups ran again.
 *
 * Entries expire after TTL because Graph's preview links are short-lived (how
 * short is not documented where we could check, so this is conservative). A
 * miss is remembered too, and retried after TTL or a reset().
 */
const THUMB_TTL_MS = 10 * 60 * 1000;

export function useThumbnailCache(webUrls: string[], getToken: () => Promise<string>) {
  const [cache, setCache] = useState<Record<string, { url: string | null; at: number }>>({});
  const inFlight = useRef(new Set<string>());
  /** Bumped by reset(), so a lookup started before it (e.g. before signing
   *  in) can't write its stale misses back afterwards. */
  const generation = useRef(0);

  useEffect(() => {
    const now = Date.now();
    const need = [...new Set(webUrls)].filter((u) =>
      !inFlight.current.has(u) && (!cache[u] || now - cache[u].at > THUMB_TTL_MS));
    if (need.length === 0) return;
    need.forEach((u) => inFlight.current.add(u));
    const gen = generation.current;
    void resolveThumbnails(need, getToken).then((found) => {
      if (gen !== generation.current) return;
      need.forEach((u) => inFlight.current.delete(u));
      setCache((prev) => {
        const next = { ...prev };
        const at = Date.now();
        for (const u of need) next[u] = { url: found[u] ?? null, at };
        return next;
      });
    });
  }, [webUrls, cache, getToken]);

  const thumbs = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [u, v] of Object.entries(cache)) if (v.url) out[u] = v.url;
    return out;
  }, [cache]);

  /** Looked up already, found or not — a miss is settled too (for "Loading…" labels). */
  const settled = useCallback((u: string) => u in cache, [cache]);

  return { thumbs, settled, reset: () => { generation.current++; inFlight.current.clear(); setCache({}); } };
}

export function useGraphToken() {
  const { instance, accounts } = useMsal();
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const silent = useCallback(async (): Promise<string> => {
    try {
      const r = await instance.acquireTokenSilent({ scopes: GRAPH_SCOPES, account: accounts[0] });
      return r.accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) setNeedsSignIn(true);
      throw new GraphConsentError('Graph consent required');
    }
  }, [instance, accounts]);
  const interactive = useCallback(async (): Promise<string> => {
    try {
      const r = await instance.acquireTokenSilent({ scopes: GRAPH_SCOPES, account: accounts[0] });
      return r.accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        const r = await instance.acquireTokenPopup({ scopes: GRAPH_SCOPES });
        setNeedsSignIn(false);
        return r.accessToken;
      }
      throw new GraphConsentError('Graph consent required');
    }
  }, [instance, accounts]);
  return { silent, interactive, needsSignIn };
}
